import { z } from 'zod';

import { SPORT_DEFAULTS, type Sport } from './enums.js';
import { toStorableJson, toStorableText } from './text.js';

/**
 * What a phone sends up from the sideline, and what it gets back.
 *
 * The governing sentence is CLAUDE.md's: "The scorebook must never lose an
 * event. Not on a dead network, not on a killed app, not on a phone restart."
 * Every choice here is downstream of it.
 */

/**
 * The largest batch the server will take in one request.
 *
 * A game is roughly 400 events (BUILD_PLAN section 7), so this is a whole game
 * and change. It exists because the inbound body is attacker-controlled and an
 * unbounded array is a memory problem, NOT to pace a real client: a phone that
 * has been offline for a whole game must be able to drain its outbox, and a cap
 * a real backlog can exceed is a cap that loses plays.
 */
export const MAX_SYNC_BATCH = 500;

/**
 * The largest a single event's `meta` may serialise to, once stored.
 *
 * BYTES via `TextEncoder`, not `String.length`: 1024 code units of emoji is
 * 2048 UTF-8 bytes.
 *
 * 1024 is a judgement, not a derivation: BUILD_PLAN 5.3 sets no length. Raising
 * it forces `MAX_META_RAW_BYTES` up with it, which is what the batch has to fit
 * the body cap through, so it is not a free product call.
 *
 * This bounds the stored form only. Wire size is unbounded independently of it,
 * so an oversized body is answered by `BATCH_TOO_LARGE` rather than prevented
 * here. See ADR-0050.
 */
export const MAX_META_BYTES = 1024;

/**
 * How large `meta` may be as SENT, before sanitising. Bounds work, not content.
 *
 * Needs headroom over `MAX_META_BYTES`, because `JSON.stringify` escapes a NUL
 * or a lone surrogate to six characters that sanitise to zero or three, so a
 * legitimate truncated note arrives larger than it lands.
 *
 * Sized so a full batch of canonical events fits `app.ts`'s 1mb parser: at 1536
 * the worst canonical batch measures 976kb, at 2048 it does not fit. A size
 * target, not a guarantee, for the reason on `MAX_META_BYTES`.
 */
export const MAX_META_RAW_BYTES = 1536;

/**
 * The rules a game is played under, frozen at kickoff.
 *
 * This mirrors `CoreGameConfig` in `packages/stats-core` field for field, and
 * that is deliberate rather than incidental: the stored value IS what the
 * engine is handed, and the engine is a pure function of events AND config.
 *
 * It is validated HERE because it is validated nowhere else, ever again. The
 * column is `jsonb` and the repository takes `unknown`, so a config that gets
 * in gets frozen, and a frozen `regulationLengthMs: null` is a division by
 * nothing inside a pure package that has no way to complain about it. Every
 * other field on a game can be corrected by appending an event. This one
 * cannot.
 *
 * The bounds are against nonsense, not against a sport. `onCourtCount` runs to
 * 11 because 11v11 is a real youth format and the engine has never validated
 * it. Period length runs to an hour because a running-clock league can play
 * one, and down to a minute because a shorter period is a typo.
 */
export const GameConfigSchema = z.object({
  periods: z.object({
    /** 2 for halves, 4 for quarters. Regulation only: overtime is not counted here. */
    regulationPeriods: z.number().int().min(1).max(10),
    regulationLengthMs: z.number().int().min(60_000).max(3_600_000),
    /**
     * Zero is allowed and means "this league does not play overtime".
     *
     * A tie stands in most youth leagues, and refusing zero would force a coach
     * to invent an overtime length for a competition that has none.
     */
    overtimeLengthMs: z.number().int().min(0).max(3_600_000),
  }),
  onCourtCount: z.number().int().min(1).max(11),
  /** Running-clock leagues do not stop for dead balls, so clock events are advisory. */
  runningClock: z.boolean(),
  /**
   * The foul rules (BUILD_PLAN 5.2), named as `packages/stats-basketball`'s
   * `BasketballGameConfig` names the first of them, which is where the box
   * score will read it once a bridge from this config exists. REQUIRED, with
   * no default: a body that omits them is refused at the door rather than
   * read as zero. For the bonus pair zero means "this league has no bonus",
   * which 5.2 asks for. For `disqualifyingFouls` zero is NOT a basketball
   * value (5.2 says 4, 5 or 6, and the box score's `pf >= disqualifyingFouls`
   * marks every player out at zero); the floor stays at zero only because
   * this schema does not know the sport and a soccer config carries zero,
   * which no basketball box score ever reads. The client offers 4, 5 and 6.
   * ADR-0065.
   */
  disqualifyingFouls: z.number().int().min(0).max(10),
  teamFoulsForBonus: z.number().int().min(0).max(30),
  teamFoulsForDoubleBonus: z.number().int().min(0).max(30),
});

export type GameConfig = z.infer<typeof GameConfigSchema>;

/**
 * The starting config for a sport, in the shape the engine reads.
 *
 * `SPORT_DEFAULTS` states a sport's defaults the way a person says them ("four
 * twelve-minute quarters"), and the engine wants milliseconds and a nested
 * period structure. This is the one conversion between the two, so a coach
 * screen and a game row cannot drift apart by rounding differently.
 *
 * Overtime defaults to the regulation length. That is wrong for most leagues
 * and right for none in particular, which is why every value here is editable
 * per game, including at the field (BUILD_PLAN 5.2).
 */
export function defaultGameConfig(sport: Sport): GameConfig {
  const defaults = SPORT_DEFAULTS[sport];
  const lengthMs = defaults.periodMinutes * 60_000;
  return {
    periods: {
      regulationPeriods: defaults.periods,
      regulationLengthMs: lengthMs,
      overtimeLengthMs: lengthMs,
    },
    onCourtCount: defaults.onCourtCount,
    runningClock: false,
    disqualifyingFouls: defaults.disqualifyingFouls,
    teamFoulsForBonus: defaults.teamFoulsForBonus,
    teamFoulsForDoubleBonus: defaults.teamFoulsForDoubleBonus,
  };
}

/**
 * A uuid in the casing the store keeps, which is lowercase.
 *
 * `z.uuid()` accepts either casing and PostgreSQL normalises a `uuid` column to
 * lowercase on the way back out, so an uppercase id survives the door and then
 * fails every JS string comparison the server makes against a stored id. Three
 * of those exist and each one is a stuck outbox rather than a cosmetic
 * mismatch: a void target is not found in `allowed` and the batch is refused
 * `unknown_void_target` forever, a real roster child is refused
 * `unknown_player`, and the `known` echo never matches what the device sent so
 * the outbox never retires the play. `crypto.randomUUID()` is already
 * lowercase, so this only ever normalises a client that went out of its way.
 *
 * Normalised at the door rather than compared case-insensitively downstream,
 * because the door is one place and the comparisons are many.
 */
const StoredUuid = z.uuid().transform((value) => value.toLowerCase());

/**
 * Is this exactly what a JSON body can carry, all the way down?
 *
 * `meta` is the one free-form field on an event, and everything downstream of
 * it assumes JSON data: `jsonb` stores what `JSON.stringify` emits and the byte
 * cap measures that same string.
 *
 * `toStorableJson` walks strings, arrays, and ANY non-null object, which is the
 * detail this check exists for. An earlier version of this sentence said it
 * walked plain objects and returned everything else unchanged, and then two
 * lines later described the case that proves otherwise, which a delta pass
 * caught: a `Date` is not plain and is not returned unchanged, it is walked,
 * and `Object.entries(new Date())` is `[]`, so it landed as `{}`. Refusing a
 * value that walk would empty is cheaper than teaching the walk about every
 * class a caller might invent.
 *
 * RECURSIVE, which the two previous versions of this check were not. Both
 * tested only the top level, so `{ at: new Date() }` was accepted and stored as
 * `{ at: {} }`. That one is worse than the top-level case it replaced, because
 * `JSON.stringify({ at: new Date() })` is `{"at":"2026-09-05T18:05:00.000Z"}`:
 * the wire would have carried it and the store would have taken it, and the
 * schema's own transform was what destroyed it.
 *
 * Numbers and `undefined` values are ACCEPTED whatever they are, and one round
 * of refusing them was wrong on both counts. `JSON.parse('{"a":1e400}')` is
 * `Infinity`, so a non-finite number arrives over the wire and refusing it is a
 * 422 the client holds and retries, trading one field that `JSON.stringify`
 * would have written as `null` for a stuck outbox. An `undefined` value is how
 * JavaScript spells "omit this key" and `JSON.stringify` does exactly that, so
 * refusing it rejects the ordinary `{ assistedBy: id ?? undefined }` idiom.
 *
 * What is refused is a value the store CANNOT hold as written: a function, a
 * symbol, a bigint (`JSON.stringify` throws on it), and any object that is not
 * a plain one.
 */
/**
 * Deepest `meta` this accepts. A note is a coach's payload, not a tree.
 *
 * Bytes do not bound depth. Nesting costs two bytes a level, so the byte caps
 * alone permit ~765 levels while `toStorableJson` overflows the stack around
 * 1950, and raising a byte cap moves both numbers. Checked here because this
 * runs before the
 * transform.
 */
export const MAX_META_DEPTH = 32;

function isJsonValue(value: unknown, depth = 0): boolean {
  if (value === null || value === undefined) return true;
  const kind = typeof value;
  if (kind === 'string' || kind === 'boolean' || kind === 'number') return true;
  if (depth >= MAX_META_DEPTH) return false;
  if (Array.isArray(value)) return value.every((item) => isJsonValue(item, depth + 1));
  if (kind === 'object') {
    return (
      Object.getPrototypeOf(value) === Object.prototype &&
      Object.values(value as Record<string, unknown>).every((item) => isJsonValue(item, depth + 1))
    );
  }
  return false;
}

/**
 * The size of `meta` AS IT ARRIVED, or null when it cannot be measured.
 *
 * **This is a WORK bound, and it has to run before anything walks the value.**
 * The cap below it measures what LANDS, which is the number a reader cares
 * about, and for three rounds that was the only cap: the chain ran
 * `refine(shape)` then `transform(toStorableJson)` then `refine(cap)`, so both
 * walks were bounded by nothing but the 1mb body parser. Three separate
 * comments in this codebase claimed the work was "bounded by
 * `MAX_META_BYTES`". It was not, and the gap was measurable rather than
 * theoretical: 5151 `meta` keys that all sanitise to the same string fit in a
 * 603KB body and held Node's event loop for 26.8 seconds, before any database
 * call and before the game was even looked up. Node is single threaded, so
 * that is every tenant, from any role in `SCOREBOOK_WRITE`, repeatable because
 * ADR-0050 declines a rate limit on this route.
 *
 * `JSON.stringify` is linear and is the cheapest thing that can answer the
 * question, but it THROWS on a value too deep to walk and on a circular or
 * bigint one. Caught here and reported as "cannot measure", because an
 * uncaught throw from inside a refinement escapes `safeParse`, and anything
 * that is not an `AppError` is INTERNAL: a 500 where a 422 belongs, on the one
 * route where a 500 means the device retries forever.
 */
function rawMetaBytes(value: unknown): number | null {
  try {
    const json = JSON.stringify(value);
    return json === undefined ? null : new TextEncoder().encode(json).length;
  } catch {
    return null;
  }
}

/**
 * A recorded play, as the device wrote it.
 *
 * `id` is the DEVICE's, generated before the event had ever been near a
 * network, and it is what makes a resend safe (BUILD_PLAN 5.3, 5.8). The server
 * neither mints it nor rewrites it.
 */
export const GameEventInputSchema = z
  .object({
    id: StoredUuid,
    /**
     * Bounded above by the column: `client_seq` is int4. A device using
     * `Date.now()` or a monotonic millisecond counter as its sequence lands
     * around 1.7e12, which is a Postgres 22003 out of range, not an
     * `AppError`, so the envelope answers INTERNAL and the outbox retries the
     * identical batch forever. Same class as the coords and self-void rules
     * below: a store constraint with no counterpart at the door is a 500.
     */
    clientSeq: z.number().int().min(0).max(2_147_483_647),
    deviceId: StoredUuid,
    period: z.number().int().min(1).max(20),
    /**
     * Milliseconds REMAINING, and NEGATIVE past the end of a period.
     *
     * There is no non-negative bound, deliberately. The clock counts down, and a
     * code that plays past the nominal period length records the overrun as a
     * negative reading. A `min(0)` here would refuse every stoppage-time goal of
     * a soccer season, and basketball would never have shown it.
     *
     * Bounded either side only against nonsense: a day of game clock is not a
     * game, it is a client bug or an attacker.
     */
    gameClockMs: z.number().int().min(-86_400_000).max(86_400_000),
    // Length-capped: `z.iso.datetime()` allows unbounded fractional seconds,
    // which made this a second field a client could grow without limit.
    wallClock: z.iso.datetime().max(32),
    /**
     * Free text, bounded, NOT an enum, and made storable before it travels.
     *
     * The type registry lives in the stats packages and grows per sport. Pinning
     * an enum here means an OLD server rejecting an event type a NEWER client
     * sent, and a rejected event is a lost play. The engine ignores a type it
     * does not recognise, which is the safe failure.
     *
     * `toStorableText` runs because this is a `text` column and a NUL in it is
     * a driver error rather than a validation error, which on this route means
     * a 500 and an outbox that retries forever. The length floor is re-checked
     * AFTER it, since a type made only of characters the store cannot hold is
     * not a type.
     */
    type: z
      .string()
      .max(64)
      .transform(toStorableText)
      .refine((value) => value.length >= 1, { message: 'type must not be empty' }),
    team: z.enum(['us', 'them']),
    playerId: StoredUuid.nullish(),
    opponentNumber: z.number().int().min(0).max(999).nullish(),
    value: z.number().int().min(-100).max(100).nullish(),
    /**
     * The type-specific payload: a coach's note text, an assist, a card reason.
     *
     * `nullish`, like every other optional field here, and it was `.optional()`
     * alone for one commit. That is a stuck outbox rather than a nit: a device
     * serialising an unset `meta` as `null` (the ordinary choice for a nullable
     * column, and what this schema accepts on all four siblings) had its whole
     * batch refused, and the route's own instruction to the client on that code
     * is to hold the batch and retry, never to drop it. So the phone would retry
     * forever and land nothing. Three separate comments and two tests named
     * "every optional field" asserted the opposite and none of them set it.
     *
     * Bounded, because it is the largest field on the event and
     * `MAX_SYNC_BATCH` of them has to fit inside the body cap. See
     * `MAX_SYNC_BATCH` for the arithmetic.
     */
    meta: z
      /*
       * A plain object, checked by hand rather than with `z.record`.
       *
       * `z.record(z.string(), z.unknown())` builds its output with plain
       * assignment, so it DROPS an own `__proto__` key before any transform
       * below can see it. The nested case was fixed in `toStorableJson` with
       * `defineProperty` and the top-level case was not, which left a schema
       * whose comment claimed the class was closed while
       * `meta: {"__proto__": "...", "text": "..."}` still landed as
       * `{"text": "..."}` with a 200 and the id in `accepted`: a device
       * retiring a play the server holds differently from how it was
       * recorded. Found by a fresh-context pass one round after the nested
       * fix, in the gap between the fix and the sentence describing it.
       *
       * The predicate tests the PROTOTYPE, not `typeof`. The first version of
       * this replacement was `typeof value === 'object' && value !== null &&
       * !Array.isArray(value)`, under a comment claiming it was what
       * `z.record` checked. It was not: `typeof` reports `object` for a
       * `Date`, a `Map`, a `Set` and every class instance, all of which
       * `z.record` refused. `toStorableJson` is deliberately total, so
       * `Object.entries(new Date())` is `[]` and the value landed as `{}`:
       * accepted, echoed in `accepted`, and the device retires a play whose
       * payload the server never received. The same silent loss this door was
       * being rewritten to close, reintroduced one line away, caught by a
       * fresh-context pass.
       *
       * The SECOND version tested only the top-level prototype, so
       * `{ at: new Date() }` was still accepted and stored as `{ at: {} }`.
       * That is worse than the case it fixed: the wire would have carried
       * `{"at":"2026-09-05T18:05:00.000Z"}` and the store would have taken it,
       * so the schema's own transform was destroying data the rest of the
       * stack handled correctly. `isJsonValue` above is recursive for that
       * reason, and the sentence "the door no longer strips anything the store
       * would have taken" is only true now that it is.
       *
       * A JSON body cannot carry a `Date`, a `Map` or a class instance, so for
       * those the HTTP door was never exposed and the rule earns its keep on
       * the CLIENT, once one exists: this package is intended as the shared
       * schema an outbox validates against before queueing (REVIEW_RUBRIC E2).
       * Nothing under `apps/web` imports it today.
       *
       * NUMBERS are the exception, and an earlier version of this comment said
       * flatly that none of these could arrive over the wire, which was wrong:
       * `JSON.parse('{"a":1e400}')` is `Infinity`. So non-finite numbers are
       * ACCEPTED here rather than refused. `JSON.stringify` turns them into
       * `null`, which is what the wire already does to them, and a refusal on
       * this route is a 422 the client is told to hold and retry, so refusing a
       * value the wire can carry trades one wrong field for a stuck outbox.
       * `undefined` values are accepted for the same reason and a better one:
       * `{ assistedBy: id ?? undefined }` is how JavaScript spells "omit this
       * key", `JSON.stringify` does exactly that, and refusing it would reject
       * the ordinary idiom. Both were briefly refused, which was the wrong
       * trade on the one route that may not lose a batch.
       */
      .unknown()
      /*
       * ONE refinement, ordered cheapest first and short-circuiting with `&&`,
       * because the order is the safety property. The size bound has to run
       * before `isJsonValue` walks the value, and separate `.refine()` calls
       * do not promise that the second is skipped when the first fails.
       *
       * `!Array.isArray` is load-bearing and not redundant: `isJsonValue`
       * accepts an array of JSON values, and `meta` is a key-value payload.
       */
      .refine(
        (value) =>
          typeof value === 'object' &&
          value !== null &&
          !Array.isArray(value) &&
          (rawMetaBytes(value) ?? Number.POSITIVE_INFINITY) <= MAX_META_RAW_BYTES &&
          isJsonValue(value),
        {
          // Names the bound this refinement actually applies, which is the
          // RAW one. It said `MAX_META_BYTES` for a round, which is the cap
          // enforced further down, so a developer reading a refusal was given
          // the wrong number. It never reaches a client: the route collapses
          // any parse failure to `{ field: 'events', code: 'invalid' }`.
          message:
            `meta must be a plain object of JSON values, at most ` +
            `${String(MAX_META_RAW_BYTES)} bytes as sent`,
        },
      )
      /*
       * Made storable BEFORE this cap, so THIS cap measures the bytes that
       * actually land rather than the ones that arrived. `jsonb` refuses a NUL
       * or an unpaired surrogate anywhere inside it, keys included, and a
       * client truncating a note to fit this very cap is how an emoji gets cut
       * in half.
       *
       * There are two caps and they answer different questions, which the
       * first version of the pair got wrong by making them the same number.
       * `MAX_META_RAW_BYTES` above bounds the WORK before any walk happens and
       * is deliberately loose; this one is the CONTENT rule and is exact. A
       * note whose stored form fits must not be refused because its escaped
       * form did not, which is precisely what the truncated-emoji case does.
       */
      .transform((value) => toStorableJson(value) as Record<string, unknown>)
      .refine((value) => new TextEncoder().encode(JSON.stringify(value)).length <= MAX_META_BYTES, {
        message: `meta must serialise to ${String(MAX_META_BYTES)} bytes or fewer`,
      })
      .nullish(),
    voidsEventId: StoredUuid.nullish(),
    /**
     * Where on the court or pitch it happened, normalised to 0..1.
     *
     * BUILD_PLAN 5.4's shot-location prompt and the `x`/`y` columns migration
     * 0012 creates. They were in the database and in the plan and in neither the
     * schema nor the insert, and Zod strips unknown keys, so a client answering
     * the location prompt got 200 with an event in `accepted` and the
     * coordinates silently gone.
     *
     * Stored, and not yet read back: `CoreEvent` has no notion of position and
     * the shot chart that will want these is not built. That is a gap in a read
     * rather than in a write, which is the recoverable direction.
     *
     * The precision matches the column, `numeric(5,4)`.
     */
    x: z.number().min(0).max(1).nullish(),
    y: z.number().min(0).max(1).nullish(),
  })
  /**
   * Both coordinates or neither, which the DATABASE also insists on.
   *
   * `game_events_coords_paired` is `CHECK ((x IS NULL) = (y IS NULL))`. Without
   * this refinement a body carrying `x` alone parses cleanly, reaches the
   * insert, and violates that constraint. A constraint violation is not an
   * `AppError`, so the envelope maps it to INTERNAL, and a 500 on the sync
   * route means the device retries the identical batch forever and lands
   * nothing for the whole game.
   *
   * Caught by the delta pass BEFORE it was reachable: the coordinates were
   * being dropped a layer earlier, so fixing that alone would have shipped the
   * 500. Half a fix is how a fix round becomes the defect.
   */
  .refine(
    (event) =>
      (event.x === null || event.x === undefined) === (event.y === null || event.y === undefined),
    { message: 'x and y must be given together', path: ['y'] },
  )
  /**
   * An event cannot void itself.
   *
   * `game_events_void_not_self` is a database CHECK, and without this the
   * payload gets all the way there: the schema had no cross-field rule, and the
   * repository's void guard treats a target arriving in the same batch as
   * legitimate, which a self-void trivially is. The constraint violation is not
   * an `AppError`, so it surfaces as INTERNAL, and a 500 on the sync route
   * means the device retries the identical batch forever and lands nothing for
   * the rest of the game.
   *
   * The second cross-field rule on this schema to be caught the same way, both
   * by a cross-model pass: a database CHECK with no counterpart at the door is
   * a 500 waiting for a client that has never read the migration.
   */
  .refine((event) => event.voidsEventId !== event.id, {
    message: 'an event cannot void itself',
    path: ['voidsEventId'],
  })
  /**
   * A voiding row's type is the sport-neutral marker `VOID`, and `VOID` rows
   * are the only rows that may name a target.
   *
   * `type` is free text on purpose, so for the family of type-only events (a
   * period marker, a clock stop, a timeout) the repository's "a void carries
   * no play" guard cannot tell a pure undo from a marker folded into a void:
   * the folded marker was accepted with a 200 and then readable by nothing,
   * because the log drops every voiding row. Pinning the convention the
   * repository's own tests always followed closes the half that guard could
   * not see. A client that folds content into a void is told to send two rows
   * (CLAUDE.md: corrections are void events plus new events), which it can do
   * in the same batch.
   */
  .refine(
    (event) =>
      event.voidsEventId === null || event.voidsEventId === undefined || event.type === 'VOID',
    {
      message: 'a voiding row carries type VOID and nothing else',
      path: ['type'],
    },
  )
  .refine(
    (event) =>
      event.type !== 'VOID' || (event.voidsEventId !== null && event.voidsEventId !== undefined),
    {
      message: 'a VOID row must name the event it voids',
      path: ['voidsEventId'],
    },
  );

export type GameEventInput = z.infer<typeof GameEventInputSchema>;

/*
 * One id may not wear two DIFFERENT events in one batch.
 *
 * `ON CONFLICT DO NOTHING` keeps the first row carrying an id and drops the
 * rest, so a second, different event under that id lands nowhere while the id
 * comes back in `accepted`. A client reads that as success and retires both,
 * and `game_events` is append-only, so the dropped one is gone. The costly pair
 * is a play plus a VOID of some OTHER event: that clears the self-void refine,
 * so the play lands and the undo vanishes.
 *
 * Identical copies are NOT refused, and that distinction is the whole design.
 * An outbox that re-enqueues after an ambiguous send drains the same event
 * twice in one batch, which loses nothing: `ON CONFLICT` writes it once and the
 * id comes back. Refusing that would 422 a batch the client is told to hold and
 * never drop, stopping a FIFO queue behind a harmless duplicate, which is the
 * outcome ADR-0054 exists to avoid.
 *
 * Serialised only for ids that actually collide, so an ordinary batch pays one
 * Map insert per event. Key order is the schema's and so stable, except inside
 * `meta`, where two orderings of equal content compare unequal and are refused.
 * That is the safe direction: it never lets a real event vanish. Compared after
 * `StoredUuid` lowercases, so one id in two cases is still one id. ADR-0056.
 */
function noIdWearsTwoEvents(events: readonly GameEventInput[]): boolean {
  const firstAt = new Map<string, number>();
  for (const [index, event] of events.entries()) {
    const seen = firstAt.get(event.id);
    if (seen === undefined) {
      firstAt.set(event.id, index);
      continue;
    }
    if (JSON.stringify(events[seen]) !== JSON.stringify(event)) return false;
  }
  return true;
}

export const SyncRequestSchema = z.object({
  events: z.array(GameEventInputSchema).max(MAX_SYNC_BATCH).refine(noIdWearsTwoEvents, {
    message: 'two different events in this batch share one id',
  }),
});

export type SyncRequest = z.infer<typeof SyncRequestSchema>;

/**
 * Closing the book on a game.
 *
 * **The score is SUPPLIED here, and that is a known gap, not a design.** It is
 * the client's arithmetic, and nothing on the server checks it against the log
 * underneath it. A wrong or stale pair writes a final score that disagrees with
 * the plays, and the `games` row is what a schedule list prints.
 *
 * It is supplied because deriving it needs a SPORT ENGINE rather than a sum. An
 * own goal counts for the other side, so `ourScore` is not "add up our players'
 * values", and `packages/stats-soccer` (which is where `scoringSideOf` lives)
 * is not on this branch yet. Wiring it is its own piece of work, tracked, and
 * the engine that will do it is pure and fully covered.
 *
 * Bounded against nonsense rather than against a sport. 999 is not a basketball
 * score or a soccer score, and a schema that knew which was which would be the
 * sport knowledge this package is not allowed to hold.
 */
export const GameFinishSchema = z.object({
  ourScore: z.number().int().min(0).max(999),
  theirScore: z.number().int().min(0).max(999),
});

export type GameFinish = z.infer<typeof GameFinishSchema>;

/**
 * Where a book is, derived from the game row and the latest transition.
 *
 * Not a database enum. `games.status` stays `scheduled | live | final`, and
 * approval is a pair of columns plus the append-only history (ADR-0139).
 * Adding an enum value would be a down migration that cannot remove it.
 */
export const BookStateSchema = z.enum([
  'open',
  'in_progress',
  'submitted',
  'published',
  'reopened',
]);
export type BookState = z.infer<typeof BookStateSchema>;

/**
 * The book a client should show.
 *
 * A final game is `submitted` until a coach approves it, then `published`.
 * Status wins over the reopened flag: a final row cannot still be reopened,
 * because finish writes a newer `submitted` transition. The flag applies only
 * while the game is not final.
 */
export function deriveBook(game: {
  readonly status: 'scheduled' | 'live' | 'final';
  readonly approvedAt: string | null;
  readonly reopened: boolean;
}): BookState {
  if (game.status === 'final') return game.approvedAt === null ? 'submitted' : 'published';
  if (game.reopened) return 'reopened';
  if (game.status === 'live') return 'in_progress';
  return 'open';
}

/** The game as the scorebook routes return it. Frozen here for the later branches. */
export const ScorebookGameSchema = z.object({
  id: z.uuid(),
  eventId: z.uuid(),
  config: z.unknown(),
  status: z.enum(['scheduled', 'live', 'final']),
  ourScore: z.number().int(),
  theirScore: z.number().int(),
  officialScoreUs: z.number().int().nullable(),
  officialScoreThem: z.number().int().nullable(),
  finalizedAt: z.iso.datetime().nullable(),
  approvedAt: z.iso.datetime().nullable(),
  submissionId: z.uuid().nullable(),
  /**
   * When the latest submission was recorded. Null when the book has never
   * been submitted, including a final game backfilled as already approved
   * with no transition row.
   */
  submittedAt: z.iso.datetime().nullable(),
  reopened: z.boolean(),
  book: BookStateSchema,
});
export type ScorebookGame = z.infer<typeof ScorebookGameSchema>;

/** Body of POST /api/scorebook/games/:gameId/approve. */
export const GameApproveSchema = z.object({
  submissionId: StoredUuid,
});
export type GameApprove = z.infer<typeof GameApproveSchema>;

/**
 * One game waiting for a coach. The acting team is the caller's, so the team
 * name is not repeated here.
 */
export const AwaitingApprovalSchema = z.object({
  gameId: z.uuid(),
  eventId: z.uuid(),
  submissionId: z.uuid(),
  officialScoreUs: z.number().int(),
  officialScoreThem: z.number().int(),
  submittedAt: z.iso.datetime(),
  opponent: z.string().nullable(),
  startsAt: z.iso.datetime(),
});
export type AwaitingApproval = z.infer<typeof AwaitingApprovalSchema>;

/**
 * What the server answers with.
 *
 * `known` and `rejected` PARTITION the ids the client sent. Retire on both:
 * that is the whole client rule, and neither bucket alone is it.
 *
 * `known` is the ids sent that this game holds. Not every id in the game: a
 * client asking what it can forget is asking about what it sent.
 *
 * `rejected` is ids that belong to a DIFFERENT game and so were written
 * nowhere. Without this bucket they fall out of both other answers and a
 * compliant client resends them forever. Returned rather than raised so one
 * bad id cannot block the other 499 plays. ADR-0054.
 *
 * `accepted` is the ids written by THIS call, and is a SUBSET OF `known`, not
 * a third disjoint bucket: `known` is read after the insert on the same
 * transaction, so anything just written is in both. Absence from `accepted`
 * means only that this call did not write it, which is the ordinary outcome of
 * a retry and is not a failure.
 *
 * The whole log comes back rather than a delta. BUILD_PLAN 5.8 describes a
 * watermark, and section 7 sizes the table at about 400 rows per game and says
 * not to prematurely optimize. A watermark here would have to be a timestamp,
 * two rows can share a millisecond, and so a cursor of "strictly after T" can
 * skip a play while "at or after T" resends forever. Both trade a lost play for
 * bandwidth this product does not need.
 */
export interface SyncResponse {
  readonly accepted: readonly string[];
  readonly known: readonly string[];
  readonly rejected: readonly string[];
}
